/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { Icon } from 'ui/atoms/Icon/Icon';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './TransferPricingComparison.module.scss';

import { type ComparisonRow } from './TransferPricingComparison.types';

type Translate = (key: string) => string;

const getRows = (t: Translate): ComparisonRow[] => [
  {
    attribute: t('Original filings'),
    us: (
      <>
        {t('The filed document')} <b>{t('sits on the field itself')}</b>
        {t(', inside the dataset, for every company in a set. Open the filing from the number you are reading.')}
      </>
    ),
    them: (
      <>
        {t('Standardised values in the dataset. Original documents are a')} <b>{t('separate ordering service')}</b>
        {t(', retrieved per company on request.')}
      </>
    ),
  },
  {
    attribute: t('How the financials are produced'),
    us: (
      <>
        {t('Collected from the registry and digitised in house. The')}{' '}
        <b>{t('document, page and filing date stay on the field')}</b>.
      </>
    ),
    them: (
      <>
        <b>{t('Licensed from 170+ information providers')}</b>{' '}
        {t(
          "alongside Moody's own sources, then treated and standardised into IFRS, local GAAP and a global standardised format — per Moody's published description.",
        )}
      </>
    ),
  },
  {
    attribute: t('Industry screening'),
    us: (
      <>
        {t('Described business, products and service language,')}{' '}
        <b>{t('with the activity code as a check rather than the gate')}</b>.
      </>
    ),
    them: t('Standard activity classification systems.'),
  },
  {
    attribute: t('Ownership and independence'),
    us: (
      <>
        {t('Shareholders with')} <b>{t('percentages and filing dates')}</b>
        {t(', and the group structure above the entity.')}
      </>
    ),
    them: (
      <>
        <span className={styles.figure}>2bn+</span>{' '}
        {t('ownership links, with a graded independence indicator derived from shareholder data.')}
      </>
    ),
  },
  {
    attribute: t('Data transparency'),
    us: (
      <>
        {t('Every attribute carries')} <b>{t('the source name, a link to the filing and a timestamp')}</b>.{' '}
        {t('Provenance is exposed at field level, not at dataset level.')}
      </>
    ),
    them: t('Values are standardised into IFRS, local GAAP and a global standardised format across sources.'),
  },
  {
    attribute: t('Data sources'),
    us: (
      <>
        {t('Collected')} <b>{t('strictly from government registries')}</b>
        {t(
          ", on direct pipelines we run ourselves. Nothing bought in from an aggregator or resold from another vendor's layer.",
        )}
      </>
    ),
    them: t('Compiled from registry filings alongside other information providers into a single dataset.'),
  },
  {
    attribute: t('Extracts inside your deliverables'),
    us: (
      <>
        {t('Redistribution in local files, benchmarking studies, APA submissions and client reports,')}{' '}
        <b>{t('written into standard terms')}</b>.
      </>
    ),
    them: t('Governed by the negotiated licence.'),
  },
];

export const TransferPricingComparison = () => {
  const { t } = useInt();
  const rows = getRows(t);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const revealables = rowRefs.current.filter((el): el is HTMLDivElement => Boolean(el));

    if (!revealables.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.revealed);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 },
    );

    revealables.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <Section className={styles.section}>
      <div className={styles.head}>
        <TitleBlock
          align="left"
          className={styles.titleBlock}
          title={t('Measured against the database this market runs on.')}
          description={t(
            'Orbis is the standard for comparables work and it is bigger than us on almost every count. We have set out where that is true as plainly as where it is not, because you will find out either way.',
          )}
        />
      </div>

      <div className={styles.table}>
        <span className={styles.accent} aria-hidden="true" />

        <div className={styles.tableHead}>
          <span />
          <span className={styles.brandUs}>
            <Icon type="logo-new-short" className={styles.brandLogo} />
            Global Database
          </span>
          <span className={styles.brandThem}>Moody&rsquo;s · Orbis</span>
        </div>

        {rows.map((row, index) => (
          <div
            key={row.attribute}
            className={styles.row}
            style={{ transitionDelay: `${index * 60}ms` }}
            ref={(el) => {
              rowRefs.current[index] = el;
            }}
          >
            <span className={styles.attribute}>{row.attribute}</span>

            <span className={cn(styles.cell, styles.cellUs)}>
              <span className={styles.cellIcon} aria-hidden="true">
                ✓
              </span>
              <span className={styles.cellText}>{row.us}</span>
            </span>

            <span className={cn(styles.cell, styles.cellThem)}>
              <span className={cn(styles.cellIcon, styles.cellIconThem)} aria-hidden="true" />
              <span className={styles.cellText}>{row.them}</span>
            </span>
          </div>
        ))}
      </div>
    </Section>
  );
};
